import React, { useState } from 'react';
import {
  Box, Paper, Typography, Grid, FormGroup, FormControlLabel, Checkbox,
  Button, Alert, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

const DEFAULT_PERMISSIONS = {
  administrator: {
    viewDashboard: true,
    viewAssets: true,
    editAssets: true,
    deleteAssets: true,
    viewWorkOrders: true,
    createWorkOrders: true,
    editWorkOrders: true,
    deleteWorkOrders: true,
    completeWorkOrders: true,
    viewPMSchedules: true,
    editPMSchedules: true,
    deletePMSchedules: true,
    viewUsers: true,
    manageUsers: true,
    viewQRCodes: true,
    emailTest: true,
  },
  supervisor: {
    viewDashboard: true,
    viewAssets: true,
    editAssets: true,
    deleteAssets: true,
    viewWorkOrders: true,
    createWorkOrders: true,
    editWorkOrders: true,
    deleteWorkOrders: true,
    completeWorkOrders: true,
    viewPMSchedules: true,
    editPMSchedules: true,
    deletePMSchedules: true,
    viewUsers: false,
    manageUsers: false,
    viewQRCodes: true,
    emailTest: false,
  },
  technician: {
    viewDashboard: true,
    viewAssets: true,
    editAssets: false,
    deleteAssets: false,
    viewWorkOrders: true,
    createWorkOrders: true,
    editWorkOrders: false,
    deleteWorkOrders: false,
    completeWorkOrders: true,
    viewPMSchedules: true,
    editPMSchedules: false,
    deletePMSchedules: false,
    viewUsers: false,
    manageUsers: false,
    viewQRCodes: false,
    emailTest: false,
  },
  operator: {
    viewDashboard: true,
    viewAssets: true,
    editAssets: false,
    deleteAssets: false,
    viewWorkOrders: true,
    createWorkOrders: true,
    editWorkOrders: false,
    deleteWorkOrders: false,
    completeWorkOrders: false,
    viewPMSchedules: true,
    editPMSchedules: false,
    deletePMSchedules: false,
    viewUsers: false,
    manageUsers: false,
    viewQRCodes: false,
    emailTest: false,
  },
};

const PERMISSION_LABELS = {
  viewDashboard: 'View Dashboard',
  viewAssets: 'View Assets',
  editAssets: 'Edit Assets',
  deleteAssets: 'Delete Assets',
  viewWorkOrders: 'View Work Orders',
  createWorkOrders: 'Create Work Orders',
  editWorkOrders: 'Edit Work Orders',
  deleteWorkOrders: 'Delete Work Orders',
  completeWorkOrders: 'Complete Work Orders',
  viewPMSchedules: 'View PM Schedules',
  editPMSchedules: 'Edit PM Schedules',
  deletePMSchedules: 'Delete PM Schedules',
  viewUsers: 'View Users',
  manageUsers: 'Manage Users (Add/Edit/Delete)',
  viewQRCodes: 'View QR Codes',
  emailTest: 'Access Email Test',
};

export default function RoleManagement() {
  const [permissions, setPermissions] = useState(() => {
    const saved = localStorage.getItem('rolePermissions');
    return saved ? JSON.parse(saved) : DEFAULT_PERMISSIONS;
  });
  const [success, setSuccess] = useState('');

  const handlePermissionChange = (role, permission) => {
    setPermissions({
      ...permissions,
      [role]: {
        ...permissions[role],
        [permission]: !permissions[role][permission],
      },
    });
  };

  const handleSave = () => {
    localStorage.setItem('rolePermissions', JSON.stringify(permissions));
    setSuccess('Permissions saved successfully! Please refresh the page for changes to take effect.');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all permissions to defaults?')) {
      setPermissions(DEFAULT_PERMISSIONS);
      localStorage.removeItem('rolePermissions');
      setSuccess('Permissions reset to defaults! Please refresh the page for changes to take effect.');
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Role Permissions Management</Typography>
        <Box>
          <Button onClick={handleReset} sx={{ mr: 2 }}>
            Reset to Defaults
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
            Save Changes
          </Button>
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure permissions for each role. Changes take effect after saving and refreshing the page.
      </Alert>

      <Grid container spacing={3}>
        {Object.keys(permissions).map((role) => (
          <Grid item xs={12} md={6} key={role}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                {role}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormGroup>
                {Object.keys(PERMISSION_LABELS).map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={permissions[role][permission]}
                        onChange={() => handlePermissionChange(role, permission)}
                      />
                    }
                    label={PERMISSION_LABELS[permission]}
                  />
                ))}
              </FormGroup>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Current Permissions Summary</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Permission</strong></TableCell>
                <TableCell align="center"><strong>Administrator</strong></TableCell>
                <TableCell align="center"><strong>Supervisor</strong></TableCell>
                <TableCell align="center"><strong>Technician</strong></TableCell>
                <TableCell align="center"><strong>Operator</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(PERMISSION_LABELS).map((permission) => (
                <TableRow key={permission}>
                  <TableCell>{PERMISSION_LABELS[permission]}</TableCell>
                  <TableCell align="center">
                    {permissions.administrator[permission] ? '✅' : '❌'}
                  </TableCell>
                  <TableCell align="center">
                    {permissions.supervisor[permission] ? '✅' : '❌'}
                  </TableCell>
                  <TableCell align="center">
                    {permissions.technician[permission] ? '✅' : '❌'}
                  </TableCell>
                  <TableCell align="center">
                    {permissions.operator[permission] ? '✅' : '❌'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
